'use client';

import { usePathname } from 'next/navigation';
import { ReactNode, useEffect } from 'react';

interface PageTransitionProps {
  children: ReactNode;
}

// Module-scoped so it survives PageTransition remounts across navigations.
// It stays false only for the very first page load of the session — the
// LCP-critical render — and flips to true once the app has mounted, so every
// subsequent client-side navigation gets the fade-in. A hard reload reloads
// this module, resetting it to false → first paint is never animated.
let hasMountedOnce = false;

export default function PageTransition({ children }: PageTransitionProps) {
  const pathname = usePathname();

  // Skip the fade-in on the first paint. Animating the whole page from
  // opacity:0 pushes the LCP element back (it only counts as "painted" once
  // visible, and this non-composited opacity animation competes with hydration
  // on slow devices → LCP ~5.7s while FCP is ~1.2s). SSR and the first client
  // render both read `false` here, so there is no hydration mismatch.
  const animate = hasMountedOnce;

  useEffect(() => {
    hasMountedOnce = true;
  }, []);

  return (
    <div
      key={pathname}
      className={
        animate
          ? 'motion-safe:animate-in motion-safe:fade-in motion-safe:duration-500'
          : undefined
      }
    >
      {children}
    </div>
  );
}
